/**
 * RICH TEXT — the shape of formatted content, and the pure questions asked of it.
 *
 * The charter (and, in time, anything else written with formatting) is stored as
 * a ProseMirror document: structured JSON, not a string, so headings, list items
 * and table cells stay addressable — which is what lets it render to a signed PDF
 * and be analysed again later. Kept out of the editor component for the reason
 * the rest of this folder is: a question the screen owns is one nothing can test.
 */
import type { JSONContent } from '@tiptap/core';

/** A formatted document, as the editor reads and writes it; stored as jsonb. */
export type RichDoc = JSONContent;

/** The empty document — what "nothing written yet" is, so a round-trip never
 *  turns real content into an error and the editor always has valid content. */
export const EMPTY_RICH_DOC: RichDoc = { type: 'doc', content: [{ type: 'paragraph' }] };

/** A rich document holding one paragraph of plain text — for turning a string
 *  (a derived rationale, an imported line) into the RichDoc a rich field stores.
 *  Empty/whitespace in gives the empty document, so "nothing" round-trips cleanly. */
export function richFromText(text: string | null | undefined): RichDoc {
  const t = (text ?? '').trim();
  if (!t) return { type: 'doc', content: [{ type: 'paragraph' }] };
  return { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: t }] }] };
}

/** Split a rich document into its top-level blocks — each paragraph, and each list
 *  item — as its own one-block RichDoc, for seeding a list (e.g. the AAR's benefits)
 *  from prose or a bullet list. Blocks with no text are dropped. */
export function richTopBlocks(doc?: RichDoc | null): RichDoc[] {
  if (!doc?.content) return [];
  const out: RichDoc[] = [];
  for (const node of doc.content) {
    if (node.type === 'bulletList' || node.type === 'orderedList') {
      for (const item of node.content ?? []) {
        const paras = (item.content ?? []).filter((n) => richHasText(n));
        if (paras.length) out.push({ type: 'doc', content: paras });
      }
    } else if (richHasText(node)) {
      out.push({ type: 'doc', content: [node] });
    }
  }
  return out;
}

/** Whether a rich document actually carries anything — an empty paragraph does
 *  not. The `.trim()` of formatted content: used to decide "is this set?". */
export function richHasText(doc?: RichDoc | null): boolean {
  if (!doc) return false;
  const walk = (n: JSONContent): boolean => {
    if (n.type === 'text' && (n.text ?? '').trim()) return true;
    // A table with cells, or a horizontal rule, is content even with no text.
    if (n.type === 'table' || n.type === 'horizontalRule') return true;
    return (n.content ?? []).some(walk);
  };
  return walk(doc);
}

/** The plain text of a rich document — for a one-line peek, a status-card
 *  summary, or anywhere a formatted body has to collapse to running text.
 *  Block boundaries become a single space so paragraphs and list items don't
 *  run together; the result is squeezed and trimmed. Not for display of the
 *  real content — that is the read-only editor — only for summaries. */
export function richText(doc?: RichDoc | null): string {
  if (!doc) return '';
  let out = '';
  const walk = (n: JSONContent): void => {
    if (n.type === 'text') out += n.text ?? '';
    (n.content ?? []).forEach(walk);
    if (n.type === 'paragraph' || n.type === 'heading' || n.type === 'listItem' || n.type === 'tableRow')
      out += ' ';
  };
  walk(doc);
  return out.replace(/\s+/g, ' ').trim();
}
